import { supabase } from '@/integrations/supabase/client';
import { PropertyPhoto, PhotoEnhancementOptions, UserProfile, RoomType } from '@/types/property';

export interface EnhancementResult {
  photos: PropertyPhoto[];
  successCount: number;
  totalCount: number;
  failedIndices: number[];
  partialSuccess: boolean;
  aborted?: boolean;
}

/**
 * Track photo enhancement usage in the database
 * Called after successful enhancement to update usage counters
 */
export async function trackEnhancementUsage(
  successCount: number,
  serviceType: 'PHOTO_EDIT' | 'AI_ADDITION' | 'VIRTUAL_STAGING' = 'PHOTO_EDIT'
): Promise<void> {
  if (successCount <= 0) return;
  
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      console.warn('No user ID available for usage tracking');
      return;
    }
    
    console.log(`📊 Tracking usage: ${successCount} ${serviceType} for user ${userId}`);
    
    // Server-side counter — enforces plan limits and prevents client tampering.
    const { data, error } = await supabase.rpc('increment_monthly_usage', {
      p_user_id: userId,
      p_service_type: serviceType,
      p_count: successCount,
    });

    if (error) {
      console.error('Failed to track usage:', error);
    } else if (data && data.length > 0) {
      console.log(`✅ Tracked ${successCount} ${serviceType} usage. New total: ${data[0].new_total}/${data[0].monthly_limit}`);
    }
  } catch (err) {
    console.error('Error tracking enhancement usage:', err);
  }
}

export async function enhancePhotosWithAI(
  photos: PropertyPhoto[],
  onProgress?: (enhanced: number, total: number, currentPhotoId?: string) => void,
  userProfile?: UserProfile | null,
  abortSignal?: AbortSignal
): Promise<EnhancementResult> {
  if (photos.length === 0) return { photos: [], successCount: 0, totalCount: 0, failedIndices: [], partialSuccess: false };

  // Convert ALL photos to base64 (removed the slice limit)
  // Handle both File objects and URL previews
  const imagePromises = photos.map(async (photo) => {
    // If photo has a file, use FileReader
    if (photo.file) {
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result as string);
        };
        reader.onerror = () => {
          // If file reading fails, try the preview URL
          console.warn('FileReader failed for photo, using preview URL');
          resolve(photo.preview);
        };
        reader.readAsDataURL(photo.file);
      });
    }
    
    // If no file but has preview, try to fetch it and convert to base64
    if (photo.preview) {
      // If preview is already a data URL, ensure it has correct MIME type
      if (photo.preview.startsWith('data:')) {
        // Fix common MIME type issues
        let dataUrl = photo.preview;
        if (dataUrl.includes('application/octet-stream')) {
          // Replace with image/jpeg as default
          dataUrl = dataUrl.replace('application/octet-stream', 'image/jpeg');
        }
        return dataUrl;
      }
      
      // Reject blob URLs - they cannot be fetched across sessions
      if (photo.preview.startsWith('blob:')) {
        console.error('Cannot process blob URL - it is session-specific and cannot be fetched:', photo.preview.substring(0, 50));
        throw new Error('Photo URL is a blob URL which cannot be processed. The original photo may have been stored incorrectly.');
      }
      
      // Try to fetch the preview URL and convert to base64
      try {
        const response = await fetch(photo.preview);
        if (response.ok) {
          const blob = await response.blob();
          
          // Determine the correct MIME type
          let mimeType = blob.type;
          if (!mimeType || mimeType === 'application/octet-stream') {
            // Try to detect from content or default to JPEG
            mimeType = 'image/jpeg';
          }
          
          return new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              let result = reader.result as string;
              // Ensure the MIME type is correct
              if (result.includes('application/octet-stream')) {
                result = result.replace('application/octet-stream', mimeType);
              }
              // If no MIME type, add one
              if (result.startsWith('data:;base64,') || result.startsWith('data:base64,')) {
                result = result.replace(/^data:;?base64,/, `data:${mimeType};base64,`);
              }
              resolve(result);
            };
            reader.readAsDataURL(blob);
          });
        } else {
          console.warn('Failed to fetch preview URL - status:', response.status);
        }
      } catch (err) {
        console.warn('Failed to fetch preview URL:', err);
      }
      
      // Last resort: return the URL directly (the edge function will try to handle it)
      // But warn if it looks like it might fail
      if (!photo.preview.startsWith('http://') && !photo.preview.startsWith('https://')) {
        console.warn('Preview URL is not an HTTP URL, may fail on server:', photo.preview.substring(0, 50));
      }
      return photo.preview;
    }
    
    throw new Error('Photo has no file or preview');
  });

  const images = await Promise.all(imagePromises);

  console.log(`Enhancing ${images.length} photos with AI...`);

  // Track progress - start with first photo being enhanced
  let enhancedCount = 0;
  onProgress?.(enhancedCount, images.length, photos[0]?.id);

  // Get enhancement options from the first photo (same for all)
  // Merge with user profile settings if available
  const profileTemplate = userProfile?.photo_enhancement_template;
  
  // Get advancedSettings: photo-level settings (from preset selection during creation) take priority over profile template
  // This ensures that if user selects a different style during creation, it overrides their saved profile style
  const advancedSettings = photos[0]?.enhancementOptions?.advancedSettings 
    ?? profileTemplate?.advancedSettings 
    ?? undefined;
  
  // IMPORTANT: addDecorations and goldenHourMode are now PER-PHOTO
  // We build a base config here, but override per-photo in invokeWithRetry
  const enhancementOptions = {
    addDecorations: false, // Will be overridden per-photo
    goldenHourMode: false, // Will be overridden per-photo
    quality: 'high',
    // Apply profile-based settings
    skyMode: profileTemplate?.skyMode,
    declutterLevel: profileTemplate?.declutterLevel,
    // advancedSettings contains the style preset (brightness, contrast, saturation, etc.)
    advancedSettings: advancedSettings,
  };
  
  // Log which style is being used
  if (advancedSettings) {
    console.log(`🎨 Using style settings: brightness=${advancedSettings.brightness}, contrast=${advancedSettings.contrast}, saturation=${advancedSettings.saturation}, warmth=${advancedSettings.warmth}`);
  } else {
    console.log('🎨 Using default balanced style (no custom settings)');
  }

  // Helper function to parse a list of photo numbers from text like "2,3,5" or "2, 3, and 5" or "2-5"
  function parsePhotoNumbers(text: string, maxPhotos: number): number[] {
    const numbers: number[] = [];
    
    // Remove "photos", "pictures", etc. and clean up
    const cleaned = text.replace(/(?:photos?|pictures?|images?|pics?)/gi, '').trim();
    
    // Split by commas, "and", "&", or spaces
    const parts = cleaned.split(/[,\s]+(?:and|&)?[,\s]*/i).filter(Boolean);
    
    for (const part of parts) {
      const trimmed = part.replace(/[#\s]/g, '');
      
      // Check for range like "2-5" or "2 to 5"
      const rangeMatch = trimmed.match(/^(\d+)\s*(?:-|to)\s*(\d+)$/i);
      if (rangeMatch) {
        const start = parseInt(rangeMatch[1], 10);
        const end = parseInt(rangeMatch[2], 10);
        for (let i = Math.min(start, end); i <= Math.max(start, end); i++) {
          if (i >= 1 && i <= maxPhotos && !numbers.includes(i)) {
            numbers.push(i);
          }
        }
      } else {
        // Single number
        const num = parseInt(trimmed, 10);
        if (!isNaN(num) && num >= 1 && num <= maxPhotos && !numbers.includes(num)) {
          numbers.push(num);
        }
      }
    }
    
    return numbers;
  }

  // Parse custom prompts if provided
  // First, check if each photo has its own individual custom prompt (per-photo mode)
  const hasPerPhotoPrompts = photos.some((photo, index) => {
    // Check if this photo has a different prompt than others (indicating per-photo mode)
    const thisPrompt = photo.enhancementOptions?.customPrompt;
    const firstPrompt = photos[0]?.enhancementOptions?.customPrompt;
    return thisPrompt && (index === 0 || thisPrompt !== firstPrompt);
  });

  // Check if all photos have the same prompt (legacy single-prompt mode)
  const allSamePrompt = photos.every(p => 
    p.enhancementOptions?.customPrompt === photos[0]?.enhancementOptions?.customPrompt
  );

  let customPrompts: string[] | undefined;
  
  if (hasPerPhotoPrompts || !allSamePrompt) {
    // Per-photo mode: each photo has its own custom prompt
    customPrompts = photos.map(photo => photo.enhancementOptions?.customPrompt?.trim() || '');
    
    console.log('=== Per-Photo Custom Prompts (Direct) ===');
    customPrompts.forEach((prompt, index) => {
      console.log(`  Photo ${index + 1}: ${prompt ? `"${prompt}"` : '(no custom prompt)'}`);
    });
    console.log('==========================================');
    
    // Check if all prompts are empty
    if (customPrompts.every(p => !p)) {
      customPrompts = undefined;
    }
  } else {
    // Legacy single-prompt mode: try to parse the complex prompt
    const customPromptText = photos[0]?.enhancementOptions?.customPrompt;
    
    if (customPromptText && customPromptText.trim()) {
      // Parse the custom prompt to extract per-photo instructions
      customPrompts = new Array(photos.length).fill('');
    
    // Global instructions to apply to all photos
    const globalInstructions: string[] = [];
    
    // Convert ordinal words to numbers
    function ordinalToNumber(word: string): number | null {
      const ordinals: { [key: string]: number } = {
        'first': 1, '1st': 1,
        'second': 2, '2nd': 2,
        'third': 3, '3rd': 3,
        'fourth': 4, '4th': 4,
        'fifth': 5, '5th': 5,
        'sixth': 6, '6th': 6,
        'seventh': 7, '7th': 7,
        'eighth': 8, '8th': 8,
        'ninth': 9, '9th': 9,
        'tenth': 10, '10th': 10,
        'last': photos.length,
      };
      return ordinals[word.toLowerCase()] || null;
    }
    
    // Extract all photo numbers from a text segment
    function extractPhotoNumbers(text: string): number[] {
      const numbers: number[] = [];
      
      // Pattern for "photo 2", "pic 3", "image 1", "#4", etc.
      const numPattern = /(?:photo|picture|image|pic|img)?\s*#?\s*(\d+)/gi;
      let match;
      while ((match = numPattern.exec(text)) !== null) {
        const num = parseInt(match[1], 10);
        if (num >= 1 && num <= photos.length && !numbers.includes(num)) {
          numbers.push(num);
        }
      }
      
      // Pattern for ordinals: "first photo", "2nd image", "the third one"
      const ordinalPattern = /(?:the\s+)?(first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|last|1st|2nd|3rd|4th|5th|6th|7th|8th|9th|10th)(?:\s+(?:photo|picture|image|pic|one))?/gi;
      while ((match = ordinalPattern.exec(text)) !== null) {
        const num = ordinalToNumber(match[1]);
        if (num && num >= 1 && num <= photos.length && !numbers.includes(num)) {
          numbers.push(num);
        }
      }
      
      // Pattern for ranges: "2-5", "photos 2 to 5", "2 through 5"
      const rangePattern = /(\d+)\s*(?:-|to|through)\s*(\d+)/gi;
      while ((match = rangePattern.exec(text)) !== null) {
        const start = parseInt(match[1], 10);
        const end = parseInt(match[2], 10);
        for (let i = Math.min(start, end); i <= Math.max(start, end); i++) {
          if (i >= 1 && i <= photos.length && !numbers.includes(i)) {
            numbers.push(i);
          }
        }
      }
      
      return numbers.sort((a, b) => a - b);
    }
    
    // Check if text indicates "all photos"
    function isGlobalInstruction(text: string): boolean {
      const globalPatterns = [
        /\b(?:all|every|each)\s+(?:photos?|pictures?|images?|pics?)\b/i,
        /\b(?:all|every|each)\s+(?:of\s+)?(?:them|the\s+photos?)\b/i,
        /\beverywhere\b/i,
      ];
      return globalPatterns.some(p => p.test(text));
    }
    
    // Remove photo references from instruction text to get the actual instruction
    function extractInstruction(text: string): string {
      return text
        // Remove "photo X", "pic 3", "#4", etc.
        .replace(/(?:to|in|on|for|from|at)?\s*(?:photo|picture|image|pic|img)?\s*#?\s*\d+/gi, '')
        // Remove ordinals with photo words
        .replace(/(?:to|in|on|for|from|at)?\s*(?:the\s+)?(first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|last|1st|2nd|3rd|4th|5th|6th|7th|8th|9th|10th)(?:\s+(?:photo|picture|image|pic|one))?/gi, '')
        // Remove "all photos" type references  
        .replace(/(?:to|in|on|for|from|at)?\s*(?:all|every|each)\s+(?:photos?|pictures?|images?|pics?)/gi, '')
        // Remove leading/trailing punctuation and whitespace
        .replace(/^[\s,;:\-–—]+|[\s,;:\-–—]+$/g, '')
        // Collapse multiple spaces
        .replace(/\s+/g, ' ')
        .trim();
    }
    
    // Smart split: Break text into segments that each target specific photos
    function smartSplit(text: string): string[] {
      const segments: string[] = [];
      
      // First try splitting by newlines
      const lines = text.split(/\n/).map(l => l.trim()).filter(Boolean);
      
      for (const line of lines) {
        // Try to split by common separators that indicate new instructions
        // Split by: comma, semicolon, period, or " and " when followed by a photo reference
        const photoRefPattern = /(?:photo|picture|image|pic|#\d|first|second|third|fourth|fifth|last|\d+(?:st|nd|rd|th))/i;
        
        // Split by punctuation or "and" followed by photo reference
        const splitPattern = /\s*[,;.]\s*(?=(?:photo|picture|image|pic|#?\d|the\s+)?(?:first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|last|\d))/i;
        const andSplit = /\s+and\s+(?=(?:photo|picture|image|pic|#?\d|the\s+)?(?:first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|last|\d))/i;
        
        let parts = line.split(splitPattern).map(p => p.trim()).filter(Boolean);
        
        // Further split by "and" if it precedes a photo reference
        parts = parts.flatMap(p => p.split(andSplit).map(s => s.trim()).filter(Boolean));
        
        segments.push(...parts);
      }
      
      return segments.length > 0 ? segments : [text];
    }
    
    // Process the input
    const segments = smartSplit(customPromptText);
    console.log('Smart split segments:', segments);
    
    for (const segment of segments) {
      const trimmed = segment.trim();
      if (!trimmed) continue;
      
      console.log(`Processing segment: "${trimmed}"`);
      
      // Check for global instruction
      if (isGlobalInstruction(trimmed)) {
        const instruction = extractInstruction(trimmed);
        console.log(`  → Global instruction: "${instruction}"`);
        if (instruction) {
          globalInstructions.push(instruction);
        }
        continue;
      }
      
      // Extract photo numbers from this segment
      const photoNums = extractPhotoNumbers(trimmed);
      const instruction = extractInstruction(trimmed);
      
      console.log(`  → Photo numbers found: [${photoNums.join(', ')}]`);
      console.log(`  → Extracted instruction: "${instruction}"`);
      
      if (photoNums.length > 0 && instruction) {
        // Apply to specific photos
        for (const num of photoNums) {
          const photoIndex = num - 1;
          if (photoIndex >= 0 && photoIndex < customPrompts.length) {
            console.log(`  → Assigning to photo ${num}: "${instruction}"`);
            if (customPrompts[photoIndex]) {
              customPrompts[photoIndex] += '. ' + instruction;
            } else {
              customPrompts[photoIndex] = instruction;
            }
          }
        }
      } else if (instruction && !photoNums.length) {
        // No photo reference found - treat as global
        globalInstructions.push(instruction);
      }
    }
    
    // Apply global instructions to all photos
    if (globalInstructions.length > 0) {
      const globalText = globalInstructions.join('. ');
      customPrompts = customPrompts.map(p => {
        if (p) {
          return p + '. ' + globalText;
        }
        return globalText;
      });
    }
    
    // Trim all prompts
    customPrompts = customPrompts.map(p => p.trim());
    
    // Log detailed parsing results
    console.log('=== Custom Prompt Parsing Results ===');
    console.log('Original input:', customPromptText);
    console.log('Parsed prompts per photo:');
    customPrompts.forEach((prompt, index) => {
      console.log(`  Photo ${index + 1}: ${prompt ? `"${prompt}"` : '(no custom prompt)'}`);
    });
    
    // Log global instructions if any
    if (globalInstructions.length > 0) {
      console.log('Global instructions applied to all photos:', globalInstructions);
    }
    console.log('=====================================');
    
    // Check if all prompts are empty, if so set to undefined
    if (customPrompts.every(p => !p)) {
      console.log('No photo-specific custom prompts parsed, will use standard enhancement');
      customPrompts = undefined;
    }
    } // End of legacy single-prompt parsing else block
  }

  // Base enhancement options (per-photo overrides applied in invokeWithRetry)
  const baseEnhancementOptions = {
    ...enhancementOptions,
    customWatermark: photos[0]?.enhancementOptions?.customWatermark,
    watermarkPosition: photos[0]?.enhancementOptions?.watermarkPosition,
    watermarkOpacity: photos[0]?.enhancementOptions?.watermarkOpacity,
    stagingStyle: photos[0]?.enhancementOptions?.stagingStyle,
  };

  console.log('Base enhancement options:', baseEnhancementOptions);

  // Helper function to call edge function with retries and exponential backoff
  async function invokeWithRetry(
    imageIndex: number,
    image: string,
    customPrompt: string | undefined,
    maxRetries: number = 3
  ): Promise<{ success: boolean; result: string; roomType?: RoomType; watermarkData?: any }> {
    // Build per-photo enhancement options
    const photoOptions = photos[imageIndex]?.enhancementOptions;
    const perPhotoEnhancementOptions = {
      ...baseEnhancementOptions,
      addDecorations: photoOptions?.addDecorations ?? false,
      goldenHourMode: photoOptions?.goldenHourMode ?? false,
    };
    
    console.log(`  Photo ${imageIndex + 1} options: staging=${perPhotoEnhancementOptions.addDecorations}, goldenHour=${perPhotoEnhancementOptions.goldenHourMode}`);

    type SingleEnhancementResult = { success: boolean; result: string; roomType?: RoomType; watermarkData?: any };

    const runSingleEnhancement = async (
      sourceImage: string,
      options: PhotoEnhancementOptions,
      label: string,
      attempts: number = maxRetries
    ): Promise<SingleEnhancementResult> => {
      for (let attempt = 1; attempt <= attempts; attempt++) {
        try {
          if (attempt > 1) {
            const delay = Math.min(2000 * Math.pow(2, attempt - 1) + Math.random() * 1000, 15000);
            console.log(`Retry ${attempt}/${attempts} for image ${imageIndex + 1} ${label} after ${Math.round(delay)}ms delay...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }

          const { data, error } = await supabase.functions.invoke('enhance-photos', {
            body: {
              images: [sourceImage],
              enhancementOptions: options,
              customPrompts: customPrompt ? [customPrompt] : undefined,
            },
          });

          if (error) {
            const errorName = error.name || '';
            const errorMessage = error.message || '';
            const isRetryable = errorMessage.includes('Failed to fetch') ||
                               errorMessage.includes('timeout') ||
                               errorMessage.includes('network') ||
                               errorMessage.includes('non-2xx') ||
                               errorMessage.includes('500') ||
                               errorMessage.includes('502') ||
                               errorMessage.includes('503') ||
                               errorMessage.includes('504') ||
                               errorName === 'FunctionsFetchError' ||
                               errorName === 'FunctionsHttpError';

            if (isRetryable && attempt < attempts) {
              console.warn(`Retryable error for image ${imageIndex + 1} ${label} (attempt ${attempt}/${attempts}):`, errorMessage);
              continue;
            }

            console.error(`Enhancement error for image ${imageIndex + 1} ${label}:`, error);
            return { success: false, result: sourceImage };
          }

          if (data?.error) {
            if (data.error.includes('rate') || data.error.includes('429')) {
              if (attempt < attempts) {
                console.warn(`Rate limited for image ${imageIndex + 1} ${label}, will retry...`);
                continue;
              }
            }
            console.error(`Data error for image ${imageIndex + 1} ${label}:`, data.error);
            return { success: false, result: sourceImage };
          }

          const enhancedImage = data?.enhancedImages?.[0];
          const roomType = data?.roomTypes?.[0] as RoomType | undefined;
          if (enhancedImage && data?.successCount > 0) {
            return {
              success: true,
              result: enhancedImage,
              roomType: roomType || 'other',
              watermarkData: data.watermarkData,
            };
          }

          if (attempt < attempts) {
            console.warn(`No valid result for image ${imageIndex + 1} ${label} (attempt ${attempt}), retrying...`);
            continue;
          }

          return { success: false, result: sourceImage, roomType: roomType || 'other' };
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Unknown error';

          if (attempt < attempts && (
            errorMessage.includes('fetch') ||
            errorMessage.includes('network') ||
            errorMessage.includes('timeout') ||
            errorMessage.includes('abort')
          )) {
            console.warn(`Network error for image ${imageIndex + 1} ${label} (attempt ${attempt}):`, errorMessage);
            continue;
          }

          console.error(`Failed to enhance image ${imageIndex + 1} ${label}:`, err);
          return { success: false, result: sourceImage };
        }
      }

      console.error(`All ${attempts} attempts failed for image ${imageIndex + 1} ${label}`);
      return { success: false, result: sourceImage };
    };

    if (perPhotoEnhancementOptions.addDecorations) {
      const baseResult = await runSingleEnhancement(
        image,
        { ...perPhotoEnhancementOptions, addDecorations: false },
        'base retouch'
      );

      if (abortSignal?.aborted) {
        return baseResult.success ? baseResult : { success: false, result: image };
      }

      const decorationResult = await runSingleEnhancement(
        baseResult.success ? baseResult.result : image,
        { ...perPhotoEnhancementOptions, goldenHourMode: false },
        'decorative staging',
        2
      );

      if (decorationResult.success) {
        return {
          ...decorationResult,
          roomType: decorationResult.roomType || baseResult.roomType,
          watermarkData: decorationResult.watermarkData || baseResult.watermarkData,
        };
      }

      if (baseResult.success) {
        console.warn(`Decorative staging failed for image ${imageIndex + 1}; keeping base retouch result but marking decorative staging as failed.`);
        return {
          success: false,
          result: baseResult.result,
          roomType: baseResult.roomType,
          watermarkData: baseResult.watermarkData,
        };
      }

      return decorationResult;
    }

    return runSingleEnhancement(image, perPhotoEnhancementOptions, 'enhancement');
  }

  // Process images ONE AT A TIME for maximum reliability
  // This prevents browser timeout issues when processing multiple images
  const allEnhancedImages: string[] = [];
  const allRoomTypes: (RoomType | undefined)[] = [];
  let totalSuccessCount = 0;
  const failedIndices: number[] = [];
  let watermarkData: { watermark: string; position: string; opacity: number } | null = null;

  // Process photos with a small concurrency pool. Decorative staging needs two
  // sequential model passes per photo, so running photos strictly one-by-one made
  // those listings feel painfully slow. A pool of 3 keeps rate limits happy while
  // cutting wall-clock time roughly 3x.
  const CONCURRENCY = 3;
  const orderedResults: Array<{ result: string; roomType?: RoomType; success: boolean; watermarkData?: any } | undefined> =
    new Array(images.length).fill(undefined);
  let nextIndex = 0;

  const runWorker = async () => {
    while (true) {
      const i = nextIndex++;
      if (i >= images.length) return;

      if (abortSignal?.aborted) {
        orderedResults[i] = { result: images[i], roomType: 'other', success: false };
        continue;
      }

      console.log(`Processing image ${i + 1}/${images.length}...`);
      const result = await invokeWithRetry(i, images[i], customPrompts?.[i]);
      orderedResults[i] = result;

      enhancedCount++;
      onProgress?.(enhancedCount, images.length, photos[i]?.id);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, images.length) }, () => runWorker())
  );

  orderedResults.forEach((result, i) => {
    const safe = result ?? { result: images[i], roomType: 'other' as RoomType, success: false };
    allEnhancedImages.push(safe.result);
    allRoomTypes.push(safe.roomType);

    if (safe.success) {
      totalSuccessCount++;
    } else {
      failedIndices.push(i);
    }

    if (!watermarkData && safe.watermarkData) {
      watermarkData = safe.watermarkData;
    }
  });

  // Apply watermark to enhanced images if watermark data is provided
  let finalImages = allEnhancedImages;
  if (watermarkData && watermarkData.watermark) {
    console.log('Applying custom watermark to images...');
    finalImages = await Promise.all(allEnhancedImages.map(async (imageUrl: string, index: number) => {
      try {
        return await applyWatermark(
          imageUrl, 
          watermarkData!.watermark, 
          watermarkData!.position as 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center', 
          watermarkData!.opacity
        );
      } catch (err) {
        console.warn(`Failed to apply watermark to image ${index + 1}:`, err);
        return imageUrl;
      }
    }));
  }

  // Map photos with enhanced versions and room types
  const enhancedPhotos = photos.map((photo, index) => ({
    ...photo,
    enhanced: finalImages[index] || photo.preview,
    roomType: allRoomTypes[index] || 'other',
  }));

  console.log(`Enhanced ${totalSuccessCount}/${photos.length} photos successfully`);
  console.log(`Room types detected: ${allRoomTypes.join(', ')}`);

  // NOTE: Usage tracking is now handled by the caller (useListingCreation)
  // after the entire listing creation process completes successfully.
  // This ensures usage is only counted when the process finishes end-to-end.

  return {
    photos: enhancedPhotos,
    successCount: totalSuccessCount,
    totalCount: photos.length,
    failedIndices,
    partialSuccess: totalSuccessCount > 0 && failedIndices.length > 0,
    aborted: abortSignal?.aborted || false,
  };
}

// Helper function to apply watermark to an image using canvas
async function applyWatermark(
  imageUrl: string,
  watermarkUrl: string,
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center' = 'bottom-right',
  opacity: number = 70
): Promise<string> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Failed to get canvas context'));
      return;
    }

    const mainImage = new Image();
    mainImage.crossOrigin = 'anonymous';
    
    mainImage.onload = () => {
      canvas.width = mainImage.width;
      canvas.height = mainImage.height;
      
      // Draw main image
      ctx.drawImage(mainImage, 0, 0);
      
      // Load and draw watermark
      const watermarkImage = new Image();
      watermarkImage.crossOrigin = 'anonymous';
      
      watermarkImage.onload = () => {
        // Calculate watermark size (max 15% of image width/height)
        const maxWatermarkWidth = canvas.width * 0.15;
        const maxWatermarkHeight = canvas.height * 0.15;
        
        let watermarkWidth = watermarkImage.width;
        let watermarkHeight = watermarkImage.height;
        
        // Scale down if too large
        if (watermarkWidth > maxWatermarkWidth) {
          const ratio = maxWatermarkWidth / watermarkWidth;
          watermarkWidth = maxWatermarkWidth;
          watermarkHeight = watermarkHeight * ratio;
        }
        if (watermarkHeight > maxWatermarkHeight) {
          const ratio = maxWatermarkHeight / watermarkHeight;
          watermarkHeight = maxWatermarkHeight;
          watermarkWidth = watermarkWidth * ratio;
        }
        
        // Calculate position
        const padding = Math.min(canvas.width, canvas.height) * 0.02;
        let x: number, y: number;
        
        switch (position) {
          case 'top-left':
            x = padding;
            y = padding;
            break;
          case 'top-right':
            x = canvas.width - watermarkWidth - padding;
            y = padding;
            break;
          case 'bottom-left':
            x = padding;
            y = canvas.height - watermarkHeight - padding;
            break;
          case 'center':
            x = (canvas.width - watermarkWidth) / 2;
            y = (canvas.height - watermarkHeight) / 2;
            break;
          case 'bottom-right':
          default:
            x = canvas.width - watermarkWidth - padding;
            y = canvas.height - watermarkHeight - padding;
            break;
        }
        
        // Apply opacity and draw watermark
        ctx.globalAlpha = opacity / 100;
        ctx.drawImage(watermarkImage, x, y, watermarkWidth, watermarkHeight);
        ctx.globalAlpha = 1;
        
        // Convert to base64
        resolve(canvas.toDataURL('image/jpeg', 0.92));
      };
      
      watermarkImage.onerror = () => {
        // If watermark fails to load, return original image
        console.warn('Failed to load watermark image');
        resolve(canvas.toDataURL('image/jpeg', 0.92));
      };
      
      watermarkImage.src = watermarkUrl;
    };
    
    mainImage.onerror = () => {
      reject(new Error('Failed to load main image'));
    };
    
    mainImage.src = imageUrl;
  });
}
